import { CheckCircle, ChevronLeft, ChevronRight, Copy, Instagram, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import collaborationService from '../../services/collaborationService';
import LoadingSpinner from '../ui/LoadingSpinner';
import Modal from '../ui/Modal';

export default function OutreachModal({ influencers, onClose }) {
  const { showToast } = useToast();

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [step, setStep] = useState(0); // 0 = setup, 1..N = sending, N+1 = done
  const [completed, setCompleted] = useState([]); // ids of influencers marked reached_out
  const [skipped, setSkipped] = useState([]); // ids skipped
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [dupWarning, setDupWarning] = useState(null); // null | { count, highestCycle }

  const TERMINAL_STATUSES = ['declined', 'ghosted', 'blacklisted', 'campaign_complete'];

  const currentInfluencer = step >= 1 && step <= influencers.length ? influencers[step - 1] : null;
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const renderedMessage =
    selectedTemplate && currentInfluencer
      ? collaborationService.renderTemplate(selectedTemplate.body, currentInfluencer)
      : '';

  // Pick the best template for a given country (country-specific > global default > any default > first)
  const selectBestTemplate = (allTemplates, country) => {
    const active = allTemplates.filter((t) => t.is_active);
    if (country) {
      const hit =
        active.find((t) => t.is_default && t.countries?.includes(country)) ||
        active.find((t) => t.countries?.includes(country));
      if (hit) return hit;
    }
    return (
      active.find((t) => t.is_default && (!t.countries || t.countries.length === 0)) ||
      active.find((t) => t.is_default) ||
      active[0] ||
      null
    );
  };

  useEffect(() => {
    setLoading(true);
    collaborationService
      .getTemplates()
      .then((data) => {
        setTemplates(data);
        const firstCountry = influencers[0]?.country || null;
        const best = selectBestTemplate(data, firstCountry);
        if (best) setSelectedTemplateId(best.id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = () => {
    navigator.clipboard.writeText(renderedMessage);
    showToast('Message copied to clipboard', 'success');
  };

  const handleOpenInstagram = () => {
    window.open(`https://www.instagram.com/${currentInfluencer.ig_username}/`, '_blank', 'noopener');
  };

  const doCreateCollaboration = async () => {
    setActionLoading(true);
    try {
      await collaborationService.createCollaboration({
        influencer_id: currentInfluencer.id,
        status: 'reached_out',
      });
      setCompleted((prev) => [...prev, currentInfluencer.id]);
      showToast(`@${currentInfluencer.ig_username} marked as reached out`, 'success');
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setActionLoading(false);
      setDupWarning(null);
      advanceStep();
    }
  };

  const handleMarkReachedOut = async () => {
    setActionLoading(true);
    try {
      const existing = await collaborationService.getCollaborations({ influencer_id: currentInfluencer.id });
      const active = existing.filter((c) => !TERMINAL_STATUSES.includes(c.status));
      if (active.length > 0) {
        const highestCycle = Math.max(...active.map((c) => c.cycle_number));
        setDupWarning({ count: active.length, highestCycle });
        setActionLoading(false);
        return;
      }
      await doCreateCollaboration();
    } catch (err) {
      showToast(err.message, 'error');
      setActionLoading(false);
      advanceStep();
    }
  };

  const handleSkip = () => {
    setDupWarning(null);
    setSkipped((prev) => [...prev, currentInfluencer.id]);
    advanceStep();
  };

  const advanceStep = () => {
    if (step >= influencers.length) {
      setStep(influencers.length + 1); // done screen
    } else {
      setStep((s) => s + 1);
    }
  };

  // Setup screen
  if (step === 0) {
    return (
      <Modal
        maxWidth="lg"
        closeOnBackdrop={false}
        onClose={onClose}
        fullHeight={false}
        className="rounded-xl shadow-2xl"
      >
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold">Send Outreach</h2>
            <p className="text-sm text-muted-foreground">
              {influencers.length} influencer{influencers.length !== 1 ? 's' : ''} selected
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Selected influencers */}
          <div>
            <p className="text-sm font-medium text-foreground/80 mb-2">Sending to</p>
            <div className="flex flex-wrap gap-2">
              {influencers.map((inf) => (
                <span key={inf.id} className="px-2 py-1 bg-muted text-foreground/80 text-sm rounded-full">
                  @{inf.ig_username}
                </span>
              ))}
            </div>
          </div>

          {/* Template picker */}
          <div>
            <label className="block text-sm font-medium text-foreground/80 mb-1">Outreach Template</label>
            {loading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : templates.length === 0 ? (
              <div className="p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg text-sm text-amber-700 dark:text-amber-300">
                No templates found. Go to the <strong>Templates</strong> tab to create one first.
              </div>
            ) : (
              <select
                value={selectedTemplateId || ''}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-input rounded-lg focus:ring-2 focus:ring-foreground"
              >
                {templates
                  .filter((t) => t.is_active)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.is_default ? ' (Default)' : ''}
                      {t.countries?.length > 0 ? ` [${t.countries.join(', ')}]` : ' [Global]'}
                    </option>
                  ))}
              </select>
            )}
          </div>

          {/* Preview */}
          {selectedTemplate && (
            <div>
              <p className="text-sm font-medium text-foreground/80 mb-1">Preview (for {influencers[0]?.name})</p>
              <div className="p-3 bg-muted/50 border border-border rounded-lg text-sm text-foreground/80 whitespace-pre-wrap max-h-40 overflow-y-auto">
                {collaborationService.renderTemplate(selectedTemplate.body, influencers[0] || '{{influencer_name}}')}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50">
            Cancel
          </button>
          <button
            disabled={!selectedTemplateId || templates.length === 0}
            onClick={() => setStep(1)}
            className="flex items-center gap-2 px-6 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            Start Outreach <ChevronRight size={16} />
          </button>
        </div>
      </Modal>
    );
  }

  // Done screen
  if (step > influencers.length) {
    return (
      <Modal
        maxWidth="md"
        closeOnBackdrop={false}
        onClose={onClose}
        fullHeight={false}
        className="rounded-xl shadow-2xl p-6 text-center"
      >
        <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Outreach Complete</h2>
        <p className="text-muted-foreground text-sm mb-6">
          {completed.length} marked as reached out · {skipped.length} skipped
        </p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm"
        >
          Done
        </button>
      </Modal>
    );
  }

  // Sending screen (step 1..N)
  return (
    <Modal maxWidth="lg" closeOnBackdrop={false} onClose={onClose} fullHeight={false} className="rounded-xl shadow-2xl">
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-4 border-b">
        <div>
          <h2 className="text-lg font-semibold">
            Influencer {step} of {influencers.length}
          </h2>
          <p className="text-sm text-pink-600 dark:text-pink-400 font-medium">@{currentInfluencer.ig_username}</p>
        </div>
        <button onClick={onClose} className="text-muted-foreground/70 hover:text-foreground">
          <X size={20} />
        </button>
      </div>

      <div className="px-6 py-5 space-y-4">
        {/* Progress bar */}
        <div className="w-full bg-accent rounded-full h-1.5">
          <div
            className="bg-foreground h-1.5 rounded-full transition-all"
            style={{ width: `${((step - 1) / influencers.length) * 100}%` }}
          />
        </div>

        {/* Profile info */}
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
          <div className="h-10 w-10 rounded-full bg-accent flex items-center justify-center text-sm font-bold text-muted-foreground flex-shrink-0">
            {currentInfluencer.name?.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="font-medium text-sm">{currentInfluencer.name}</p>
            <p className="text-xs text-muted-foreground">
              {[currentInfluencer.city, currentInfluencer.country].filter(Boolean).join(', ')}
              {currentInfluencer.follower_count &&
                ` · ${(currentInfluencer.follower_count / 1000).toFixed(1)}K followers`}
            </p>
          </div>
        </div>

        {/* Country-specific template suggestion */}
        {currentInfluencer.country &&
          (() => {
            const better = selectBestTemplate(templates, currentInfluencer.country);
            if (better && better.id !== selectedTemplateId) {
              return (
                <div className="flex items-center justify-between p-2.5 bg-blue-50 dark:bg-blue-950/50 border border-blue-100 dark:border-blue-900 rounded-lg text-xs">
                  <span className="text-blue-700 dark:text-blue-300">
                    Better template for <strong>{currentInfluencer.country}</strong>: <em>{better.name}</em>
                  </span>
                  <button
                    onClick={() => setSelectedTemplateId(better.id)}
                    className="ml-3 text-blue-700 dark:text-blue-300 font-medium hover:underline flex-shrink-0"
                  >
                    Switch
                  </button>
                </div>
              );
            }
            return null;
          })()}

        {/* Duplicate collaboration warning */}
        {dupWarning && (
          <div className="p-3 bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900 rounded-lg">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-1">
              Active collaboration exists (Cycle #{dupWarning.highestCycle})
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
              This influencer already has {dupWarning.count} active collaboration{dupWarning.count > 1 ? 's' : ''}.
              Starting outreach will create a new cycle.
            </p>
            <div className="flex gap-2">
              <button
                onClick={doCreateCollaboration}
                disabled={actionLoading}
                className="flex-1 py-1.5 text-xs bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {actionLoading ? 'Creating…' : 'Create New Cycle Anyway'}
              </button>
              <button
                onClick={handleSkip}
                className="px-3 py-1.5 text-xs border border-input rounded-lg hover:bg-muted/50"
              >
                Skip
              </button>
            </div>
          </div>
        )}

        {/* Message */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
            <p className="text-sm font-medium text-foreground/80">Message</p>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <Copy size={12} /> Copy
            </button>
          </div>
          <div className="p-3 bg-muted/50 border border-border rounded-lg text-sm text-foreground/80 whitespace-pre-wrap max-h-48 overflow-y-auto">
            {renderedMessage || <span className="text-muted-foreground/70 italic">No message template selected</span>}
          </div>
        </div>

        {/* Open Instagram */}
        <button
          onClick={handleOpenInstagram}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-input rounded-lg text-sm hover:bg-muted/50 transition"
        >
          <Instagram size={16} className="text-pink-500" />
          Open @{currentInfluencer.ig_username} on Instagram
        </button>
      </div>

      {/* Footer actions */}
      <div className="px-6 pb-5 flex gap-3">
        {step > 1 && (
          <button
            onClick={() => setStep((s) => s - 1)}
            className="flex items-center gap-1 px-3 py-2 text-sm border border-input rounded-lg hover:bg-muted/50"
          >
            <ChevronLeft size={16} /> Back
          </button>
        )}
        <button onClick={handleSkip} className="px-4 py-2 text-sm border border-input rounded-lg hover:bg-muted/50">
          Skip
        </button>
        <button
          onClick={handleMarkReachedOut}
          disabled={actionLoading}
          className="flex-1 flex items-center justify-center gap-2 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          {actionLoading ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              Mark as Reached Out <ChevronRight size={16} />
            </>
          )}
        </button>
      </div>
    </Modal>
  );
}
